import React from 'react';

const PALETTE = ['#2d66e2', '#f59e0b', '#10b981', '#dc2626', '#8b5cf6', '#06b6d4', '#64748b'];

export const sevColor = { Low: '#10b981', Medium: '#f59e0b', High: '#ea580c', Critical: '#dc2626' };

export function LineChart({ data, series, height = 180 }) {
  // data: [{label, ...values}], series: [{key, label, color}]
  const W = 600, H = height, P = { l: 34, r: 8, t: 8, b: 22 };
  if (!data || data.length === 0) return <div className="muted">No data</div>;
  const max = Math.max(1, ...data.flatMap((d) => series.map((s) => Number(d[s.key]) || 0)));
  const x = (i) => P.l + (data.length === 1 ? 0 : (i * (W - P.l - P.r)) / (data.length - 1));
  const y = (v) => P.t + (1 - v / max) * (H - P.t - P.b);
  const step = Math.max(1, Math.ceil(data.length / 7));
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="sec-chart" role="img">
      {[0, 0.5, 1].map((f) => (
        <g key={f}>
          <line x1={P.l} x2={W - P.r} y1={y(max * f)} y2={y(max * f)} stroke="#e2e8f0" />
          <text x={P.l - 4} y={y(max * f) + 4} textAnchor="end" fontSize="10" fill="#718096">
            {Math.round(max * f)}
          </text>
        </g>
      ))}
      {series.map((s, si) => (
        <polyline
          key={s.key} fill="none" strokeWidth="2" stroke={s.color || PALETTE[si % PALETTE.length]}
          points={data.map((d, i) => `${x(i)},${y(Number(d[s.key]) || 0)}`).join(' ')}
        />
      ))}
      {data.map((d, i) => i % step === 0 && (
        <text key={i} x={x(i)} y={H - 6} textAnchor="middle" fontSize="10" fill="#718096">{d.label}</text>
      ))}
    </svg>
  );
}

export function BarList({ items, color = '#2d66e2', colorOf }) {
  // items: [{label, value}]
  if (!items || items.length === 0) return <div className="muted">No data</div>;
  const max = Math.max(1, ...items.map((i) => i.value));
  return (
    <div className="sec-bars">
      {items.map((i) => (
        <div className="sec-bar-row" key={i.label}>
          <span className="sec-bar-label" title={i.label}>{i.label}</span>
          <div className="sec-bar-track">
            <div className="sec-bar-fill" style={{ width: `${(i.value / max) * 100}%`, background: colorOf ? colorOf(i.label) : color }} />
          </div>
          <span className="sec-bar-val">{i.value}</span>
        </div>
      ))}
    </div>
  );
}

export function Donut({ items, size = 140 }) {
  const total = items.reduce((a, b) => a + b.value, 0);
  if (!total) return <div className="muted">No data</div>;
  const r = 50, c = 2 * Math.PI * r;
  let off = 0;
  return (
    <div className="sec-donut">
      <svg viewBox="0 0 120 120" width={size} height={size}>
        <g transform="rotate(-90 60 60)">
          {items.map((it, i) => {
            const len = (it.value / total) * c;
            const el = (
              <circle key={it.label} cx="60" cy="60" r={r} fill="none" strokeWidth="18"
                stroke={it.color || PALETTE[i % PALETTE.length]}
                strokeDasharray={`${len} ${c - len}`} strokeDashoffset={-off} />
            );
            off += len;
            return el;
          })}
        </g>
        <text x="60" y="64" textAnchor="middle" fontSize="16" fontWeight="700" fill="#1a2a4a">{total}</text>
      </svg>
      <ul className="sec-legend">
        {items.map((it, i) => (
          <li key={it.label}>
            <i style={{ background: it.color || PALETTE[i % PALETTE.length] }} />
            {it.label} <b>{it.value}</b>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function Heatmap({ days, matrix }) {
  const max = Math.max(1, ...matrix.flat());
  return (
    <div className="sec-heat">
      <div className="sec-heat-row sec-heat-head">
        <span />
        {Array.from({ length: 24 }, (_, h) => <span key={h}>{h % 3 === 0 ? h : ''}</span>)}
      </div>
      {matrix.map((row, d) => (
        <div className="sec-heat-row" key={d}>
          <span>{days[d]}</span>
          {row.map((v, h) => (
            <i key={h} title={`${days[d]} ${h}:00 UTC — ${v}`}
               style={{ background: `rgba(45,102,226,${v ? 0.12 + 0.88 * (v / max) : 0.04})` }} />
          ))}
        </div>
      ))}
    </div>
  );
}

export function SevBadge({ value }) {
  return <span className="sec-badge" style={{ background: sevColor[value] || '#64748b' }}>{value}</span>;
}
export function ActionBadge({ value }) {
  const c = { Blocked: '#dc2626', Monitored: '#f59e0b', Allowed: '#10b981' }[value] || '#64748b';
  return <span className="sec-pill" style={{ color: c, borderColor: c }}>{value}</span>;
}
